<template>
  <div class="fp-outer">
    <div class="fp-trigger-bar">
      <slot name="actions" />
      <button class="fp-fields-btn" @click="open = !open">
        Fields
        <font-awesome-icon :icon="['fas', 'caret-square-down']" />
      </button>
    </div>

    <transition name="fp-slide">
      <div v-if="open" class="fp-fields-panel">
        <div class="fp-fields-col">
          <div class="fp-fields-col-header">
            <span class="fp-fields-col-title">Shown Columns</span>
            <button class="fp-fields-reset" @click="onReset" title="Reset to default">
              <font-awesome-icon :icon="['fas', 'undo']" />
            </button>
            <button class="fp-fields-close" @click="open = false">
              <font-awesome-icon :icon="['fas', 'times-circle']" class="text-danger" />
            </button>
          </div>
          <draggable class="fp-drag-list" :list="localShown" group="fp-fields" @change="onDragChange">
            <div v-for="(col, i) in localShown" :key="col[fieldKey]" class="fp-drag-item fp-drag-item--active"
              :class="{ 'fp-drag-item--locked': col.locked }">
              <font-awesome-icon :icon="['fas', 'grip-vertical']" class="fp-drag-handle" />
              <span class="fp-drag-index">{{ i + 1 }}.</span>
              {{ col[titleKey] }}
            </div>
          </draggable>
        </div>

        <div class="fp-fields-divider"></div>

        <div class="fp-fields-col">
          <div class="fp-fields-col-header">
            <span class="fp-fields-col-title">Hidden Columns</span>
          </div>
          <draggable class="fp-drag-list" :list="localHidden" group="fp-fields" @change="onDragChange">
            <div v-for="col in localHidden" :key="col[fieldKey]" class="fp-drag-item">
              <font-awesome-icon :icon="['fas', 'grip-vertical']" class="fp-drag-handle" />
              {{ col[titleKey] }}
            </div>
          </draggable>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
  name: 'FieldsPanel',
  components: { draggable },
  props: {
    shownCols: { type: Array, default: () => [] },
    hiddenCols: { type: Array, default: () => [] },
    titleKey: { type: String, default: 'label' },
    fieldKey: { type: String, default: 'key' },
  },
  data() {
    return {
      open: false,
      localShown: [],
      localHidden: [],
    };
  },
  watch: {
    shownCols: {
      immediate: true,
      handler(v) { this.localShown = v ? [...v] : []; },
    },
    hiddenCols: {
      immediate: true,
      handler(v) { this.localHidden = v ? [...v] : []; },
    },
  },
  methods: {
    onDragChange() {
      this.$emit('update:shownCols', [...this.localShown]);
      this.$emit('update:hiddenCols', [...this.localHidden]);
    },
    onReset() {
      this.open = false;
      this.$emit('reset');
    },
  },
};
</script>

<style scoped>
.fp-outer {
  display: flex;
  flex-direction: column;
}

.fp-trigger-bar {
  display: flex;
  justify-content: flex-end;
  padding: 6px 0 0;
}

.fp-fields-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #dc2626;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #dc2626;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s;
  font-family: inherit;
  white-space: nowrap;
}

.fp-fields-btn:hover {
  background: #fee2e2;
}

.fp-fields-panel {
  display: flex;
  background: #ffffff;
  border: 1px solid #e8eaed;
  border-radius: 8px;
  flex-shrink: 0;
  max-height: 320px;
  margin-top: 6px;
  overflow: hidden;
}

.fp-fields-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 12px 16px;
}

.fp-fields-col-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.fp-fields-col-title {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
  flex: 1;
}

.fp-fields-reset,
.fp-fields-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  color: #6b7280;
  font-size: 13px;
}

.fp-fields-reset:hover {
  color: #0e1b2c;
}

.fp-fields-divider {
  width: 1px;
  background: #e8eaed;
  flex-shrink: 0;
  margin: 12px 0;
}

.fp-drag-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-content: flex-start;
  min-height: 48px;
}

.fp-drag-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: #f9fafb;
  border: 1px solid #e8eaed;
  border-radius: 6px;
  font-size: 12px;
  color: #374151;
  cursor: grab;
  user-select: none;
  white-space: nowrap;
  transition: background 0.1s, border-color 0.1s;
}

.fp-drag-item:hover {
  background: #f0f1f3;
  border-color: #d1d5db;
}

.fp-drag-item--active {
  background: #0e1b2c;
  color: #ffffff;
  border-color: #0e1b2c;
}

.fp-drag-item--active:hover {
  background: #1a2e47;
  border-color: #1a2e47;
}

.fp-drag-item--locked {
  opacity: 0.6;
  cursor: not-allowed;
}

.fp-drag-handle {
  color: inherit;
  opacity: 0.4;
  font-size: 11px;
}

.fp-drag-index {
  font-size: 10px;
  opacity: 0.6;
}

.fp-slide-enter-active,
.fp-slide-leave-active {
  transition: max-height 0.2s ease, opacity 0.2s ease;
  overflow: hidden;
}

.fp-slide-enter,
.fp-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.fp-slide-enter-to,
.fp-slide-leave {
  max-height: 320px;
  opacity: 1;
}
</style>

<style>
html.dark .fp-fields-btn {
  color: #e87c7c;
  border-color: #e87c7c;
}

html.dark .fp-fields-btn:hover {
  background: rgba(220, 38, 38, 0.15);
}

html.dark .fp-fields-panel {
  background: #0e1b2c;
  border-color: rgba(255, 255, 255, 0.08);
}

html.dark .fp-fields-col-title {
  color: rgba(214, 228, 240, 0.5);
}

html.dark .fp-fields-reset,
html.dark .fp-fields-close {
  color: rgba(214, 228, 240, 0.5);
}

html.dark .fp-fields-reset:hover {
  color: #d6e4f0;
}

html.dark .fp-fields-divider {
  background: rgba(255, 255, 255, 0.1);
}

html.dark .fp-drag-item {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #d6e4f0;
}

html.dark .fp-drag-item:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
}

html.dark .fp-drag-item--active {
  background: #1a3354;
  border-color: #2a4a6e;
  color: #d6e4f0;
}

html.dark .fp-drag-item--active:hover {
  background: #1f3d62;
  border-color: #2f5580;
}
</style>
